import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaPlus,
  FaTimes,
  FaEdit,
  FaTrash,
  FaCheck,
  FaSearch,
} from "react-icons/fa";
import Select from "react-select";
import axiosClient from "../../../api-client/AxiosClient";
import { toast } from "react-toastify";
import { DashboardStats } from "./DashboardStats";
import { DashboardBottom } from "./DashboardBottom";
import { KanbanBoard } from "./KanbanBoard";
import {
  priorityOptions,
  priorityTextToNumber,
  getPriorityInfo,
  statusTextToNumber,
  statusNumberToText,
  getStatusBadge,
  getTeamMemberName,
  getAssignedByName,
} from "./taskUtils";
import {
  TaskModal,
  DeleteConfirmModal,
  ChildTaskModal,
  TaskDetailModal,
} from "./TaskModal";
import WorkItemIcon from "./Workitemicon";

const TaskManagement = () => {
  const { ToDo } = JSON.parse(
    sessionStorage.getItem("rolesAndPermission") || "{}",
  );

  const navigate = useNavigate();
  const location = useLocation();

  const [tasks, setTasks] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [selectedTaskForDetail, setSelectedTaskForDetail] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [isEditingDetail, setIsEditingDetail] = useState(false);
  const [updatingTask, setUpdatingTask] = useState(false);
  const [editingDescriptionMode, setEditingDescriptionMode] = useState(false);
  const [editingDescriptionValue, setEditingDescriptionValue] = useState("");
  const [childTasks, setChildTasks] = useState([]);
  const [loadingChildTasks, setLoadingChildTasks] = useState(false);
  const [showChildTaskModal, setShowChildTaskModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const [taskTypes, setTaskTypes] = useState([]);
  const [selectedUserFilter, setSelectedUserFilter] = useState(null);
  const [dashboardFilter, setDashboardFilter] = useState("all");
  const [modules, setModules] = useState([]);

  const [childTaskFormData, setChildTaskFormData] = useState({
    assignedTo: null,
    subject: "",
    description: "",
    priority: null,
    dueDate: "",
  });

  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assignedToFilter, setAssignedToFilter] = useState(null);
  const [assignedByFilter, setAssignedByFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentUserId, setCurrentUserId] = useState(0);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingRowData, setEditingRowData] = useState(null);
  const [savingRowId, setSavingRowId] = useState(null);

  const [activeView, setActiveView] = useState("dashboard"); // "dashboard" | "board" | "list"
  const [workItemMenuOpen, setWorkItemMenuOpen] = useState(false);

  const [taskFormData, setTaskFormData] = useState({
    assignedTo: null,
    subject: "",
    description: "",
    priority: null,
    dueDate: "",
    taskType: null, // ← ADD
  });

  const handleDeleteTaskRow = async (taskId) => {
    setTaskToDelete(taskId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;

    setDeletingTaskId(taskToDelete);
    try {
      await axiosClient.delete(`/api/Task/${taskToDelete}`);
      toast.success("Task deleted successfully!");

      // Remove from local state immediately
      setTasks((prevTasks) => prevTasks.filter((t) => t.id !== taskToDelete));

      if (selectedTaskForDetail?.id === taskToDelete) {
        handleCloseTaskDetail();
      }

      setShowDeleteConfirm(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    } finally {
      setDeletingTaskId(null);
    }
  };

  // Add fetch function (near fetchTaskTypes)
  const fetchModules = async () => {
    try {
      const response = await axiosClient.get("/api/Task/modules");
      setModules(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching modules:", error);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".work-item-dropdown")) setWorkItemMenuOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const fetchTaskTypes = async () => {
    try {
      const response = await axiosClient.get("/api/Task/TaskType");
      if (response?.data) {
        setTaskTypes(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching task types:", error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setTaskToDelete(null);
  };

  const handleStartEditRow = (task, e) => {
    e.stopPropagation();
    setEditingRowId(task.id);
    setEditingRowData({
      assignedToId: task.assignedToId,
      assignedById: task.assignedById,
      priority: priorityTextToNumber(task.priority),
      status: statusTextToNumber(task.status),
      startDate: task.startDate
        ? new Date(task.startDate).toISOString().split("T")[0]
        : "", // ← ADD

      dueDate: task.dueDate
        ? new Date(task.dueDate).toISOString().split("T")[0]
        : "",
    });
  };

  const handleCancelEditRow = () => {
    setEditingRowId(null);
    setEditingRowData(null);
  };

  // FIXED: Properly sync UI state after updates
  const handleAutoSaveField = async (task, field, value) => {
    setSavingRowId(task.id);

    try {
      const updatedData = { ...editingRowData, [field]: value };
      setEditingRowData(updatedData);

      const priorityValue =
        field === "priority"
          ? value
          : updatedData.priority || priorityTextToNumber(task.priority);
      const statusValue =
        field === "status"
          ? value
          : updatedData.status || statusTextToNumber(task.status);
      const assignedById =
        field === "assignedById"
          ? value
          : updatedData.assignedById || task.assignedById;
      const assignedToId =
        field === "assignedTo"
          ? value
          : updatedData.assignedToId || task.assignedToId;
      const dueDateValue =
        field === "dueDate"
          ? new Date(value).toISOString()
          : new Date(updatedData.dueDate || task.dueDate).toISOString();

      const startDateValue =
        field === "startDate"
          ? new Date(value).toISOString()
          : new Date(updatedData.startDate || task.startDate).toISOString();

      const payload = {
        Id: String(task.id),
        ParentId: String(task.parentid ?? 0),
        TypeId: task.typeId || "0",
        AssignedBy: String(assignedById),
        AssignedTo: String(assignedToId),
        Subject: task.subject?.trim() || "Untitled Task",
        Description: task.description?.trim() || "",
        Priority: Number(priorityValue),
        IsRead: Boolean(task.isRead),
        Status: Number(statusValue),
        DueDate: dueDateValue,
        StartDate: task.startDate
          ? new Date(task.startDate).toISOString()
          : new Date().toISOString(),
        EndDate: task.endDate
          ? new Date(task.endDate).toISOString()
          : new Date().toISOString(),
        Efforts: task.efforts ?? 0,
        IsDeleted: Boolean(task.isDeleted),
        CreatedBy: String(task.createdBy || "0"),
        UpdatedBy: String(currentUserId || "0"),
      };

      await axiosClient.put(`/api/Task/${task.id}`, payload);

      // CRITICAL FIX: Update local state immediately for UI sync
      setTasks((prevTasks) =>
        prevTasks.map((t) =>
          t.id === task.id
            ? {
                ...t,
                priority: getPriorityInfo(payload.Priority).text,
                status: statusNumberToText(payload.Status),
                assignedToId: Number(assignedToId),
                assignedById: Number(assignedById),
                dueDate: dueDateValue,
                startDate: startDateValue,
              }
            : t,
        ),
      );

      // Update detail modal if it's open for this task
      if (selectedTaskForDetail?.id === task.id) {
        setSelectedTaskForDetail((prev) => ({
          ...prev,
          priority: getPriorityInfo(payload.Priority).text,
          status: statusNumberToText(payload.Status),
          assignedToId: Number(assignedToId),
          assignedById: Number(assignedById),
          dueDate: dueDateValue,
        }));
      }

      toast.success("Task updated successfully!", { autoClose: 2000 });
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error(error.response?.data?.title || "Failed to update task");
      // Revert optimistic update on error
      await fetchTasks();
    } finally {
      setSavingRowId(null);
    }
  };

  const fetchUsersByCorporation = async () => {
    try {
      const response = await axiosClient.get("/api/User/all");
      if (response?.data) {
        const users = Array.isArray(response.data) ? response.data : [];
        const activeUsers = users.filter((user) => user.isactive === true);
        setUsersList(activeUsers);
      } else {
        setUsersList([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsersList([]);
    }
  };

  const fetchChildTasks = async (parentTaskId) => {
    setLoadingChildTasks(true);
    try {
      const response = await axiosClient.get(
        `/api/Task/allchilditems/${parentTaskId}`,
      );
      if (response?.data) {
        setChildTasks(Array.isArray(response.data) ? response.data : []);
      } else {
        setChildTasks([]);
      }
    } catch (error) {
      console.error("Error fetching child tasks:", error);
      setChildTasks([]);
    } finally {
      setLoadingChildTasks(false);
    }
  };

  const handleOpenChildTaskModal = () => {
    setShowChildTaskModal(true);
    setChildTaskFormData({
      assignedTo: null,
      subject: "",
      description: "",
      priority: null,
      dueDate: "",
    });
  };

  const handleCloseChildTaskModal = () => {
    setShowChildTaskModal(false);
    setChildTaskFormData({
      assignedTo: null,
      subject: "",
      description: "",
      priority: null,
      dueDate: "",
    });
  };

  const handleChildTaskFormChange = (field, value) => {
    setChildTaskFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateChildTask = async () => {
    if (
      !childTaskFormData.assignedTo ||
      !childTaskFormData.subject ||
      !childTaskFormData.priority
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setUpdatingTask(true);
    try {
      let dueDateString;
      if (childTaskFormData.dueDate) {
        const now = new Date();
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const seconds = now.getSeconds();

        dueDateString = `${childTaskFormData.dueDate}T${String(hours).padStart(
          2,
          "0",
        )}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
          2,
          "0",
        )}`;
      } else {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 7);
        const year = dueDate.getFullYear();
        const month = String(dueDate.getMonth() + 1).padStart(2, "0");
        const day = String(dueDate.getDate()).padStart(2, "0");
        const hours = String(dueDate.getHours()).padStart(2, "0");
        const minutes = String(dueDate.getMinutes()).padStart(2, "0");
        const seconds = String(dueDate.getSeconds()).padStart(2, "0");
        dueDateString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      }

      const payload = {
        id: "0",
        parentid: selectedTaskForDetail.id,
        assignedBy: currentUserId ? String(currentUserId) : "0",
        assignedTo: childTaskFormData.assignedTo.value,
        subject: childTaskFormData.subject,
        description: childTaskFormData.description || "",
        priority: childTaskFormData.priority.value,
        isRead: false,
        status: 1,
        dueDate: dueDateString,
        isDeleted: false,
      };

      console.log("Child task payload being sent:", payload);
      await axiosClient.post("/api/Task", payload);
      toast.success("Child task created successfully!");
      handleCloseChildTaskModal();
      fetchChildTasks(selectedTaskForDetail.id);
      // Refresh main tasks list to update dependent task count
      await fetchTasks();
    } catch (error) {
      console.error("Error creating child task:", error);
      toast.error("Failed to create child task");
    } finally {
      setUpdatingTask(false);
    }
  };

  const handleStartEditDescription = () => {
    setEditingDescriptionMode(true);
    setEditingDescriptionValue(selectedTaskForDetail.description || "");
  };

  const handleSaveDescription = async () => {
    if (!editingDescriptionValue.trim()) {
      toast.error("Description cannot be empty");
      return;
    }

    setUpdatingTask(true);
    try {
      const updatedTask = { ...selectedTaskForDetail };
      updatedTask.description = editingDescriptionValue.trim();

      const assignedById =
        updatedTask.assignedById ||
        updatedTask.assignedBy ||
        currentUserId ||
        0;
      const assignedToId =
        updatedTask.assignedToId || updatedTask.assignedTo || 0;

      const priorityValue = priorityTextToNumber(
        updatedTask.priority !== null &&
          updatedTask.priority !== undefined &&
          updatedTask.priority !== ""
          ? updatedTask.priority
          : (selectedTaskForDetail.priority ?? 2),
      );

      const statusValue = statusTextToNumber(
        updatedTask.status !== null &&
          updatedTask.status !== undefined &&
          updatedTask.status !== ""
          ? updatedTask.status
          : selectedTaskForDetail.status,
      );

      const payload = {
        Id: String(updatedTask.id ?? 0),
        ParentId: String(updatedTask.parentid ?? 0),
        TypeId: updatedTask.typeId || "0",
        AssignedBy: String(assignedById),
        AssignedTo: String(assignedToId),
        Subject:
          updatedTask.subject?.trim() ||
          selectedTaskForDetail.subject ||
          "Untitled Task",
        Description: updatedTask.description,
        Priority: Number.isNaN(priorityValue) ? 2 : priorityValue,
        IsRead: Boolean(updatedTask.isRead),
        Status: statusValue,
        DueDate: updatedTask.dueDate
          ? new Date(updatedTask.dueDate).toISOString()
          : new Date().toISOString(),
        StartDate: updatedTask.startDate
          ? new Date(updatedTask.startDate).toISOString()
          : new Date().toISOString(),
        EndDate: updatedTask.endDate
          ? new Date(updatedTask.endDate).toISOString()
          : new Date().toISOString(),
        Efforts: updatedTask.efforts ?? 0,
        IsDeleted: Boolean(updatedTask.isDeleted),
        CreatedBy: String(updatedTask.createdBy || "0"),
        UpdatedBy: String(currentUserId || "0"),
      };

      await axiosClient.put(`/api/Task/${payload.Id}`, payload);

      // Update local state
      setSelectedTaskForDetail(updatedTask);
      setEditingDescriptionMode(false);

      // Update tasks list
      setTasks((prevTasks) =>
        prevTasks.map((t) =>
          t.id === updatedTask.id
            ? { ...t, description: updatedTask.description }
            : t,
        ),
      );

      toast.success("Description updated successfully!");
    } catch (error) {
      console.error("Error updating description:", error);
      toast.error(
        error.response?.data?.title || "Failed to update description",
      );
    } finally {
      setUpdatingTask(false);
    }
  };

  const fetchComments = async (taskId) => {
    setLoadingComments(true);
    try {
      const response = await axiosClient.get(`/api/Task/allcomments/${taskId}`);
      if (response?.data) {
        const commentsList = Array.isArray(response.data) ? response.data : [];

        const mappedComments = commentsList.map((comment) => ({
          ...comment,
          createdon: comment.createdAt,
          updatedon: comment.updatedAt,
        }));

        const reversedComments = [...mappedComments].reverse();
        setComments(reversedComments);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleOpenTaskDetail = (task) => {
    navigate(`/task/edit/${task.id}`, { state: { fromView: activeView } });
  };

  const handleCloseTaskDetail = () => {
    setShowTaskDetailModal(false);
    setSelectedTaskForDetail(null);
    setComments([]);
    setNewComment("");
    setIsEditingDetail(false);
    setChildTasks([]);
  };

  // FIXED: Properly sync state after updates from detail modal
  const handleUpdateTaskFromDetail = async (field, value) => {
    setUpdatingTask(true);
    try {
      const updatedTask = { ...selectedTaskForDetail };

      if (field === "assignedTo") {
        updatedTask.assignedToId = value;
      } else if (field === "assignedBy") {
        updatedTask.assignedById = value;
      } else if (field === "priority") {
        updatedTask.priority = value;
      } else if (field === "status") {
        updatedTask.status = statusNumberToText(value);
      } else if (field === "dueDate") {
        updatedTask.dueDate = new Date(value).toISOString();
      }

      const assignedById =
        updatedTask.assignedById ||
        updatedTask.assignedBy ||
        currentUserId ||
        0;
      const assignedToId =
        updatedTask.assignedToId || updatedTask.assignedTo || 0;

      const priorityValue = priorityTextToNumber(
        updatedTask.priority !== null &&
          updatedTask.priority !== undefined &&
          updatedTask.priority !== ""
          ? updatedTask.priority
          : (selectedTaskForDetail.priority ?? 2),
      );

      const statusValue = statusTextToNumber(
        updatedTask.status !== null &&
          updatedTask.status !== undefined &&
          updatedTask.status !== ""
          ? updatedTask.status
          : selectedTaskForDetail.status,
      );

      const payload = {
        Id: String(updatedTask.id ?? 0),
        ParentId: String(updatedTask.parentid ?? 0),
        TypeId: updatedTask.typeId || "0",
        AssignedBy: String(assignedById),
        AssignedTo: String(assignedToId),
        Subject:
          updatedTask.subject?.trim() ||
          selectedTaskForDetail.subject ||
          "Untitled Task",
        Description:
          updatedTask.description?.trim() ||
          selectedTaskForDetail.description ||
          "No description provided",
        Priority: Number(priorityValue),
        IsRead: Boolean(updatedTask.isRead),
        Status: Number(statusValue),
        DueDate: updatedTask.dueDate
          ? new Date(updatedTask.dueDate).toISOString()
          : new Date().toISOString(),
        StartDate: updatedTask.startDate
          ? new Date(updatedTask.startDate).toISOString()
          : new Date().toISOString(),
        EndDate: updatedTask.endDate
          ? new Date(updatedTask.endDate).toISOString()
          : new Date().toISOString(),
        Efforts: updatedTask.efforts ?? 0,
        IsDeleted: Boolean(updatedTask.isDeleted),
        CreatedBy: String(updatedTask.createdBy || "0"),
        UpdatedBy: String(currentUserId || "0"),
      };

      await axiosClient.put(`/api/Task/${payload.Id}`, payload);

      // CRITICAL FIX: Update both detail modal and main tasks list
      const finalUpdatedTask = {
        ...updatedTask,
        assignedById: assignedById,
        assignedToId: assignedToId,
        priority: getPriorityInfo(payload.Priority).text,
        status: statusNumberToText(payload.Status),
      };

      setSelectedTaskForDetail(finalUpdatedTask);

      setTasks((prevTasks) =>
        prevTasks.map((t) => (t.id === updatedTask.id ? finalUpdatedTask : t)),
      );

      toast.success("Task updated successfully!");
    } catch (error) {
      console.error("❌ Error updating task:", error);
      console.error("Response:", error.response?.data);
      toast.error(error.response?.data?.title || "Failed to update task");
    } finally {
      setUpdatingTask(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    setSubmittingComment(true);
    try {
      const payload = {
        id: "0",
        taskid: selectedTaskForDetail.id,
        userid: "0",
        comments: newComment.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await axiosClient.post("/api/Task/Comments", payload);
      toast.success("Comment added successfully!");

      setNewComment("");

      const newCommentObj = {
        id: Date.now(),
        taskId: selectedTaskForDetail.id,
        userId: "0",
        comments: payload.comments,
        createdAt: payload.createdAt,
        updatedAt: payload.updatedAt,
        createdon: payload.createdAt,
        updatedon: payload.updatedAt,
      };

      setComments((prevComments) => [newCommentObj, ...prevComments]);

      fetchComments(selectedTaskForDetail.id);
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const fetchTasksByAssignedBy = async () => {
    const userId = assignedByFilter.value === "me" ? 0 : assignedByFilter.value;
    const response = await axiosClient.get(`/api/Task/assigned-by/${userId}`);

    if (assignedToFilter && response?.data) {
      const allTasks = Array.isArray(response.data) ? response.data : [];
      const targetUserId =
        assignedToFilter.value === "me"
          ? currentUserId
          : assignedToFilter.value;
      return allTasks.filter((task) => task.assignedToId === targetUserId);
    }

    return Array.isArray(response?.data) ? response.data : [];
  };

  const fetchTasksByAssignedTo = async () => {
    if (assignedToFilter.value === "me") {
      const response = await axiosClient.get(`/api/Task/assigned-to`);
      return Array.isArray(response?.data) ? response.data : [];
    }

    const response = await axiosClient.get(`/api/Task/all`);
    if (response?.data) {
      const allTasks = Array.isArray(response.data) ? response.data : [];
      return allTasks.filter(
        (task) => task.assignedToId === assignedToFilter.value,
      );
    }

    return [];
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      let fetchedTasks = [];

      if (assignedByFilter) {
        fetchedTasks = await fetchTasksByAssignedBy();
      } else if (assignedToFilter) {
        fetchedTasks = await fetchTasksByAssignedTo();
      } else {
        // For dashboard/board, fetch all tasks so user filter works
        // For list view, fetch only current user's tasks (default behavior)
        const response = await axiosClient.get(`/api/Task/all`);
        fetchedTasks = Array.isArray(response?.data) ? response.data : [];
      }

      setTasks(fetchedTasks);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setTasks([]);
      toast.error("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  const getViewFilteredTasks = () => {
    let filtered = tasks;

    // Apply user filter
    if (selectedUserFilter && selectedUserFilter.value !== "all") {
      filtered = filtered.filter(
        (task) => task.assignedToId === selectedUserFilter.value,
      );
    }

    // Apply dashboard tile filter
    if (dashboardFilter && dashboardFilter !== "all") {
      if (dashboardFilter === "inprogress") {
        filtered = filtered.filter((t) => statusTextToNumber(t.status) === 2);
      } else if (dashboardFilter === "closed") {
        filtered = filtered.filter((t) => statusTextToNumber(t.status) === 3);
      } else if (dashboardFilter === "highpriority") {
        filtered = filtered.filter(
          (t) => priorityTextToNumber(t.priority) === 1,
        );
      } else if (dashboardFilter === "duesoon") {
        const now = new Date();
        filtered = filtered.filter((t) => {
          if (!t.dueDate) return false;
          const diff = (new Date(t.dueDate) - now) / (1000 * 60 * 60 * 24);
          return diff >= 0 && diff <= 7;
        });
      }
    }

    return filtered;
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    const userId = userData?.id || 0;
    setCurrentUserId(userId);
    fetchUsersByCorporation();
    fetchTaskTypes();
    fetchModules();

    // Restore view from navigation state
    if (location.state?.activeView) {
      setActiveView(location.state.activeView);
    }
  }, []);

  useEffect(() => {
    if (currentUserId !== null) {
      fetchTasks();
    }
  }, [assignedToFilter, assignedByFilter, currentUserId, activeView]);

  // Reset selectedUserFilter when switching away from dashboard/board
  // Reset selectedUserFilter when switching away from dashboard/board
  useEffect(() => {
    if (activeView === "list") {
      setSelectedUserFilter(null);
      setDashboardFilter("all");
    }
  }, [activeView]);

  const getFilteredTasks = () => {
    let filtered = [...tasks];

    if (statusFilter !== "all") {
      filtered = filtered.filter((task) => {
        const statusNum = statusTextToNumber(task.status);
        if (statusFilter === "new") {
          return statusNum === 1;
        } else if (statusFilter === "inprogress") {
          return statusNum === 2;
        } else if (statusFilter === "closed") {
          return statusNum === 3;
        }
        return true;
      });
    }

    if (priorityFilter !== "all") {
      filtered = filtered.filter((task) => {
        const taskPriority = priorityTextToNumber(task.priority);
        return taskPriority === priorityFilter;
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (task) =>
          task.subject?.toLowerCase().includes(query) ||
          task.description?.toLowerCase().includes(query) ||
          String(task.id).includes(query) ||
          String(task.displayId).includes(query),
      );
    }

    return filtered;
  };

  const handleClearFilters = () => {
    setAssignedToFilter(null);
    setAssignedByFilter(null);
    setStatusFilter("all");
    setPriorityFilter("all");
    setSearchQuery("");
  };

  const getViewIconClass = () => {
    if (activeView === "dashboard") return "fa-chart-line";
    if (activeView === "board") return "fa-layer-group";
    return "fa-list";
  };

  const getSelectOptionBgColor = (isSelected, isFocused) => {
    if (isSelected) return "#2563EB";
    if (isFocused) return "#EFF6FF";
    return "white";
  };

  const handleCloseModal = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
    setTaskFormData({
      assignedTo: null,
      subject: "",
      description: "",
      priority: null,
      dueDate: "",
      taskType: null, // ← ADD
    });
  };

  const handleFormChange = (field, value) => {
    setTaskFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateTask = async () => {
    if (
      !taskFormData.assignedTo ||
      !taskFormData.subject ||
      !taskFormData.priority ||
      !taskFormData.taskType // ← ADD taskType here
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      let dueDateString;
      if (taskFormData.dueDate) {
        const now = new Date();
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const seconds = now.getSeconds();

        dueDateString = `${taskFormData.dueDate}T${String(hours).padStart(
          2,
          "0",
        )}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
          2,
          "0",
        )}`;
      } else {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + 7);
        const year = dueDate.getFullYear();
        const month = String(dueDate.getMonth() + 1).padStart(2, "0");
        const day = String(dueDate.getDate()).padStart(2, "0");
        const hours = String(dueDate.getHours()).padStart(2, "0");
        const minutes = String(dueDate.getMinutes()).padStart(2, "0");
        const seconds = String(dueDate.getSeconds()).padStart(2, "0");
        dueDateString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      }

      const payload = {
        id: "0",
        parentid: "0",
        typeId: taskFormData.taskType?.value || "", // ← ADD
        assignedBy: currentUserId ? String(currentUserId) : "0",
        assignedTo: taskFormData.assignedTo.value,
        subject: taskFormData.subject,
        description: taskFormData.description || "",
        priority: taskFormData.priority.value,
        isRead: false,
        status: 1,
        dueDate: dueDateString,
        isDeleted: false,
      };

      console.log("Payload being sent:", payload);
      await axiosClient.post("/api/Task", payload);
      toast.success("Task created successfully!");
      handleCloseModal();
      await fetchTasks();
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error("Failed to create task");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTask = async () => {
    if (
      !taskFormData.assignedTo ||
      !taskFormData.subject ||
      !taskFormData.priority
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const dueDate = taskFormData.dueDate
        ? new Date(taskFormData.dueDate)
        : new Date(selectedTask.dueDate);

      const payload = {
        Id: String(selectedTask.id),
        ParentId: String(selectedTask.parentid || 0),
        TypeId: taskFormData.taskType?.value || selectedTask.typeId || "",
        AssignedBy: String(
          selectedTask.assignedById || selectedTask.assignedBy,
        ),
        AssignedTo: String(taskFormData.assignedTo.value),
        Subject: taskFormData.subject,
        Description: taskFormData.description || "",
        Priority: taskFormData.priority.value,
        IsRead: selectedTask.isRead,
        Status: selectedTask.status,
        DueDate: dueDate.toISOString(),
        StartDate: selectedTask.startDate
          ? new Date(selectedTask.startDate).toISOString()
          : new Date().toISOString(),
        EndDate: selectedTask.endDate
          ? new Date(selectedTask.endDate).toISOString()
          : new Date().toISOString(),
        Efforts: selectedTask.efforts ?? 0,
        IsDeleted: false,
        CreatedBy: String(selectedTask.createdBy || "0"),
        UpdatedBy: String(currentUserId || "0"),
      };

      await axiosClient.put(`/api/Task/${selectedTask.id}`, payload);
      toast.success("Task updated successfully!");
      handleCloseModal();
      await fetchTasks();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    } finally {
      setLoading(false);
    }
  };

  const userOptions = usersList.map((user) => ({
    value: user.id,
    label: `${user.first_name} ${user.last_name}`,
  }));

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "new", label: "New" },
    { value: "inprogress", label: "In Progress" },
    { value: "closed", label: "Closed" },
    { value: "blocked", label: "Blocked" },
    { value: "on hold", label: "On Hold" },
    {value: "resolved", label: "Resolved"}

  ];

  const priorityFilterOptions = [
    { value: "all", label: "All Priorities" },
    { value: 1, label: "High" },
    { value: 2, label: "Medium" },
    { value: 3, label: "Low" },
  ];

  const filteredTasks = getFilteredTasks();

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fade-in {
          animation: fadeIn 0.2s ease-in-out;
        }

        .animate-scale-in {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>

      <div
        className="w-full flex flex-col"
        style={{ height: "calc(100vh - 130px)", overflow: "hidden" }}
      >
        {/* ── TOP HEADER ── */}

        <div className="bg-white border-b border-gray-100 shadow-sm">
          <div className="px-6 py-4 flex items-center justify-between gap-4">
            {/* Left: title only */}
            <div className="flex items-center gap-2 min-w-0 flex-nowrap">
              <i
                className={`fas ${getViewIconClass()} text-blue-600 text-sm`}
              />
              <h1 className="text-2xl font-black text-gray-900 tracking-tight whitespace-nowrap leading-none">
                {activeView === "dashboard" && "Dashboard"}
                {activeView === "board" && "Kanban Board"}
                {activeView === "list" && "Task Management"}
              </h1>
              {/* <span className="text-gray-300 text-sm hidden md:inline">|</span> */}
              {/* <span className="text-xs text-gray-400 font-medium hidden md:inline truncate">
                {activeView === "dashboard" &&
                  "Monitor and manage all your tasks at a glance"}
                {activeView === "board" &&
                  "Drag and drop to organize your workflow"}
                {activeView === "list" &&
                  "Manage and track all your tasks in one place"}
              </span> */}
            </div>

            {/* Right: tabs + button */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* View Tabs */}
              <div className="flex gap-1 bg-gray-100 p-0.5 rounded-lg">
                {[
                  { key: "dashboard", icon: "fas fa-home", label: "Dashboard" },
                  { key: "board", icon: "fas fa-layer-group", label: "Board" },
                  { key: "list", icon: "fas fa-list", label: "List" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveView(tab.key)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold transition-all ${
                      activeView === tab.key
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-gray-600 hover:text-blue-600 hover:bg-white"
                    }`}
                  >
                    <i className={tab.icon} />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* User Filter — Dashboard & Board only */}
              {(activeView === "dashboard" || activeView === "board") && (
                <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200">
                  <i className="fas fa-user text-gray-400 text-xs" />
                  <Select
                    value={selectedUserFilter}
                    onChange={(option) =>
                      setSelectedUserFilter(
                        option?.value === "all" ? null : option,
                      )
                    }
                    options={usersList.map((u) => ({
                      value: u.id,
                      label: `${u.first_name} ${u.last_name}`,
                    }))}
                    placeholder="All Users"
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      control: (p) => ({
                        ...p,
                        minHeight: "30px",
                        fontSize: "12px",
                        border: "none",
                        boxShadow: "none",
                        background: "transparent",
                        minWidth: "150px",
                        cursor: "pointer",
                      }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),  
                      indicatorSeparator: () => ({ display: "none" }),
                      dropdownIndicator: (p) => ({ ...p, padding: "0 4px" }),
                      valueContainer: (p) => ({ ...p, padding: "0 4px" }),
                      placeholder: (p) => ({
                        ...p,
                        color: "#6B7280",
                        fontSize: "12px",
                      }),
                      singleValue: (p) => ({
                        ...p,
                        fontSize: "12px",
                        fontWeight: 600,
                      }),
                      menu: (p) => ({
                        ...p,
                        borderRadius: "10px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        border: "1px solid #E5E7EB",
                        minWidth: "180px",
                      }),
                      option: (p, s) => {
                        const bgColor = getSelectOptionBgColor(
                          s.isSelected,
                          s.isFocused,
                        );
                        return {
                          ...p,
                          fontSize: "12px",
                          backgroundColor: bgColor,
                          color: s.isSelected ? "white" : "#374151",
                          fontWeight: s.isSelected ? 600 : 400,
                          padding: "8px 12px",
                        };
                      },
                    }}
                  />
                </div>
              )}

              {/* New Work Item dropdown */}
              <div className="relative work-item-dropdown">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setWorkItemMenuOpen((v) => !v);
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold text-sm shadow-sm"
                >
                  <FaPlus className="text-xs" /> New Work Item
                  <i className="fas fa-chevron-down text-[10px] ml-0.5" />
                </button>
                {workItemMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 min-w-[180px] overflow-hidden">
                    {[
                      { type: "bug", label: "Bug" },
                      { type: "user-story", label: "User Story" },
                      { type: "feature", label: "Feature" },
                      { type: "task", label: "Task" },
                      { type: "epic", label: "Epic" },
                    ].map((item) => (
                      <button
                        key={item.type}
                        onClick={() => {
                          setWorkItemMenuOpen(false);
                          navigate(`/task/create?type=${item.type}`);
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-700 border-b border-gray-100 last:border-0 transition"
                      >
                        <WorkItemIcon type={item.type} size={15} />
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filters row — only in list view */}
          {activeView === "list" && (
            <div className="px-6 pb-2.5 pt-0 space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                  <input
                    type="text"
                    placeholder="Search by subject, description or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-xs"
                  />
                </div>
                <Select
                  value={assignedToFilter}
                  onChange={setAssignedToFilter}
                  options={userOptions}
                  placeholder="Assigned To"
                  isClearable
                  styles={{
                    control: (p) => ({
                      ...p,
                      minHeight: "34px",
                      fontSize: "12px",
                    }),
                  }}
                />
                <Select
                  value={assignedByFilter}
                  onChange={setAssignedByFilter}
                  options={userOptions}
                  placeholder="Assigned By"
                  isClearable
                  styles={{
                    control: (p) => ({
                      ...p,
                      minHeight: "34px",
                      fontSize: "12px",
                    }),
                  }}
                />
                <Select
                  value={statusOptions.find((o) => o.value === statusFilter)}
                  onChange={(option) => setStatusFilter(option?.value || "all")}
                  options={statusOptions}
                  placeholder="Status"
                  isClearable
                  styles={{
                    control: (p) => ({
                      ...p,
                      minHeight: "34px",
                      fontSize: "12px",
                    }),
                  }}
                />
                <Select
                  value={priorityFilterOptions.find(
                    (o) => o.value === priorityFilter,
                  )}
                  onChange={(option) =>
                    setPriorityFilter(option?.value || "all")
                  }
                  options={priorityFilterOptions}
                  placeholder="Priority"
                  isClearable
                  styles={{
                    control: (p) => ({
                      ...p,
                      minHeight: "34px",
                      fontSize: "12px",
                    }),
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="text-gray-500">
                  Showing {filteredTasks.length} of {tasks.length} tasks
                </div>
                {(assignedToFilter ||
                  assignedByFilter ||
                  statusFilter !== "all" ||
                  priorityFilter !== "all" ||
                  searchQuery) && (
                  <button
                    onClick={handleClearFilters}
                    className="text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1"
                  >
                    <FaTimes className="text-xs" /> Clear Filters
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="px-4 md:px-6 py-3 md:py-4 flex-1 overflow-y-auto pb-16">
          {/* ── DASHBOARD VIEW ── */}
          {activeView === "dashboard" && (
            <>
              <DashboardStats
                tasks={
                  selectedUserFilter
                    ? tasks.filter(
                        (t) => t.assignedToId === selectedUserFilter.value,
                      )
                    : tasks
                }
                onFilterClick={(f) =>
                  setDashboardFilter(f === dashboardFilter ? "all" : f)
                }
                activeFilter={dashboardFilter}
              />
              <DashboardBottom
                tasks={getViewFilteredTasks()}
                usersList={usersList}
                modules={modules} // ← add this
                onOpenDetail={handleOpenTaskDetail}
                onViewAll={() => setActiveView("list")}
              />
            </>
          )}

          {activeView === "board" && (
            <KanbanBoard
              tasks={getViewFilteredTasks()}
              usersList={usersList}
              onOpenDetail={handleOpenTaskDetail}
              // currentUserId={currentUserId}
            />
          )}

          {/* ── LIST VIEW ── */}
          {activeView === "list" && (
            <div
              style={{
                background: "var(--color-background-primary)",
                borderRadius: "var(--border-radius-lg)",
                border: "0.5px solid var(--color-border-tertiary)",
                overflow: "hidden",
              }}
            >
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading tasks...</span>
                </div>
              )}

              {!loading && filteredTasks.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg font-medium">No tasks found</p>
                  <p className="text-sm mt-2">
                    {tasks.length === 0
                      ? "Create a new task to get started"
                      : "Try adjusting your filters"}
                  </p>
                </div>
              )}

              {!loading && filteredTasks.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Assigned To
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Assigned By
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Priority
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Start Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Due Date
                        </th>
                        {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Sub Tasks
                        </th> */}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          {ToDo?.canDelete ? "Actions" : "Edit"}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredTasks.map((task, index) => {
                        const isEditing = editingRowId === task.id;
                        const isSaving = savingRowId === task.id;

                        return (
                          <React.Fragment key={task.id}>
                            <tr
                              className="hover:bg-gray-50 transition-colors cursor-pointer"
                              onClick={() =>
                                !isEditing && handleOpenTaskDetail(task)
                              }
                            >
                              <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                                {task.displayId ?? index + 1}
                              </td>

                              <td className="px-6 py-4">
                                <p className="text-sm font-medium text-gray-900 mb-0">
                                  {task.subject}
                                </p>
                              </td>

                              <td
                                className="px-6 py-4"
                                onClick={(e) =>
                                  isEditing && e.stopPropagation()
                                }
                              >
                                {isEditing ? (
                                  <Select
                                    value={
                                      usersList.some(
                                        (u) =>
                                          u.id ===
                                          (editingRowData.assignedToId ||
                                            task.assignedToId),
                                      )
                                        ? {
                                            value:
                                              editingRowData.assignedToId ||
                                              task.assignedToId,
                                            label: getTeamMemberName(
                                              {
                                                ...task,
                                                assignedToId:
                                                  editingRowData.assignedToId ||
                                                  task.assignedToId,
                                              },
                                              usersList,
                                            ),
                                          }
                                        : null
                                    }
                                    onChange={(selected) => {
                                      if (selected) {
                                        handleAutoSaveField(
                                          task,
                                          "assignedTo",
                                          selected.value,
                                        );
                                      }
                                    }}
                                    options={usersList.map((user) => ({
                                      value: user.id,
                                      label: `${user.first_name} ${user.last_name}`,
                                    }))}
                                    isSearchable
                                    isDisabled={isSaving}
                                    menuPortalTarget={document.body}
                                    menuPosition="absolute"
                                    menuPlacement="auto"
                                    styles={{
                                      control: (provided) => ({
                                        ...provided,
                                        minHeight: "36px",
                                        fontSize: "14px",
                                      }),
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                  />
                                ) : (
                                  <span className="text-sm text-gray-700">
                                    {getTeamMemberName(task, usersList)}
                                  </span>
                                )}
                              </td>

                              <td className="px-6 py-4">
                                <span className="text-sm text-gray-700">
                                  {getAssignedByName(task, usersList)}
                                </span>
                              </td>

                              <td
                                className="px-6 py-4"
                                onClick={(e) =>
                                  isEditing && e.stopPropagation()
                                }
                              >
                                {isEditing ? (
                                  <Select
                                    value={priorityOptions.find(
                                      (p) =>
                                        p.value ===
                                        (editingRowData.priority ||
                                          priorityTextToNumber(task.priority)),
                                    )}
                                    onChange={(selected) => {
                                      if (selected) {
                                        handleAutoSaveField(
                                          task,
                                          "priority",
                                          selected.value,
                                        );
                                      }
                                    }}
                                    options={priorityOptions}
                                    isSearchable
                                    isDisabled={isSaving}
                                    menuPortalTarget={document.body}
                                    menuPosition="absolute"
                                    menuPlacement="auto"
                                    styles={{
                                      control: (provided) => ({
                                        ...provided,
                                        minHeight: "36px",
                                        fontSize: "14px",
                                      }),
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                  />
                                ) : (
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                                      getPriorityInfo(task.priority).color
                                    }`}
                                  >
                                    {getPriorityInfo(task.priority).text}
                                  </span>
                                )}
                              </td>

                              <td
                                className="px-6 py-4"
                                onClick={(e) =>
                                  isEditing && e.stopPropagation()
                                }
                              >
                                {isEditing ? (
                                  <select
                                    value={
                                      editingRowData.status ||
                                      statusTextToNumber(task.status)
                                    }
                                    onChange={(e) =>
                                      handleAutoSaveField(
                                        task,
                                        "status",
                                        Number(e.target.value),
                                      )
                                    }
                                    disabled={isSaving}
                                    className="px-3 py-1 text-sm border border-gray-300 "
                                  >
                                    <option value="1">New</option>
                                    <option value="2">In Progress</option>
                                    <option value="3">Closed</option>
                                    <option value="4">Blocked</option>
                                    <option value="5">On Hold</option>
                                    <option value="6">Resolved</option>
                                  </select>
                                ) : (
                                  getStatusBadge(task.status)
                                )}
                              </td>

                              <td
                                className="px-6 py-4"
                                onClick={(e) =>
                                  isEditing && e.stopPropagation()
                                }
                              >
                                {isEditing ? (
                                  <input
                                    type="date"
                                    value={
                                      editingRowData.startDate ||
                                      (task.startDate
                                        ? new Date(task.startDate)
                                            .toISOString()
                                            .split("T")[0]
                                        : "")
                                    }
                                    onChange={(e) =>
                                      handleAutoSaveField(
                                        task,
                                        "startDate",
                                        e.target.value,
                                      )
                                    }
                                    onClick={(e) => e.target.showPicker?.()}
                                    disabled={isSaving}
                                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                  />
                                ) : (
                                  <span className="text-sm text-gray-700">
                                    {task.startDate
                                      ? new Date(
                                          task.startDate,
                                        ).toLocaleDateString()
                                      : "N/A"}
                                  </span>
                                )}
                              </td>

                              {/* Due Date td - should come BEFORE Type td */}
                              <td
                                className="px-6 py-4"
                                onClick={(e) =>
                                  isEditing && e.stopPropagation()
                                }
                              >
                                {isEditing ? (
                                  <input
                                    type="date"
                                    value={
                                      editingRowData.dueDate ||
                                      (task.dueDate
                                        ? new Date(task.dueDate)
                                            .toISOString()
                                            .split("T")[0]
                                        : "")
                                    }
                                    onChange={(e) =>
                                      handleAutoSaveField(
                                        task,
                                        "dueDate",
                                        e.target.value,
                                      )
                                    }
                                    onClick={(e) => e.target.showPicker?.()}
                                    disabled={isSaving}
                                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                  />
                                ) : (
                                  <span className="text-sm text-gray-700">
                                    {new Date(
                                      task.dueDate,
                                    ).toLocaleDateString()}
                                  </span>
                                )}
                              </td>

                              {/* Sub Tasks td */}
                              {/* <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  {task.dependentTaskCount > 0 ? (
                                    <span className="inline-flex items-center px-3 py-1  text-xs font-medium  text-blue-800 ">
                                      {task.dependentTaskCount}{" "}
                                      {task.dependentTaskCount === 1
                                        ? "Task"
                                        : "Tasks"}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-3 py-1 text-xs font-medium  text-gray-600 ">
                                      No Sub Tasks
                                    </span>
                                  )}
                                </div>
                              </td> */}

                              {/* Type td - LAST before Actions */}
                              <td className="px-6 py-4">
                                {task.type ? (
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                                      task.type === "Bug"
                                        ? "bg-red-50 text-red-700 border-red-200"
                                        : "bg-blue-50 text-blue-700 border-blue-200"
                                    }`}
                                  >
                                    {task.type}
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400">
                                    —
                                  </span>
                                )}
                              </td>

                              <td
                                className="px-6 py-4"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex items-center gap-2">
                                  {isSaving && (
                                    <div className="flex items-center gap-2 text-blue-600">
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                      <span className="text-xs">Saving...</span>
                                    </div>
                                  )}
                                  {isEditing && (
                                    <button
                                      onClick={() => handleCancelEditRow()}
                                      className="p-2 rounded-lg bg-green-100 hover:bg-green-200 text-green-600 transition-colors"
                                      title="Done Editing"
                                    >
                                      <FaCheck className="text-sm" />
                                    </button>
                                  )}
                                  {!isEditing && (
                                    <>
                                      <button
                                        onClick={(e) =>
                                          handleStartEditRow(task, e)
                                        }
                                        className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-600 transition-colors"
                                        title="Edit Task"
                                      >
                                        <FaEdit className="text-sm" />
                                      </button>
                                      {ToDo?.canDelete && (
                                        <button
                                          onClick={() =>
                                            handleDeleteTaskRow(task.id)
                                          }
                                          className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-colors"
                                          title="Delete Task"
                                        >
                                          <FaTrash className="text-sm" />
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmModal
        showDeleteConfirm={showDeleteConfirm}
        deletingTaskId={deletingTaskId}
        handleConfirmDelete={handleConfirmDelete}
        handleCancelDelete={handleCancelDelete}
      />

      <TaskModal
        showTaskModal={showTaskModal}
        modalMode={selectedTask ? "edit" : "create"}
        taskFormData={taskFormData}
        usersList={usersList}
        loading={loading}
        handleCloseModal={handleCloseModal}
        handleFormChange={handleFormChange}
        handleCreateTask={handleCreateTask}
        handleUpdateTask={handleUpdateTask}
        taskTypes={taskTypes}
      />

      <TaskDetailModal
        showTaskDetailModal={showTaskDetailModal}
        selectedTaskForDetail={selectedTaskForDetail}
        usersList={usersList}
        isEditingDetail={isEditingDetail}
        updatingTask={updatingTask}
        editingDescriptionMode={editingDescriptionMode}
        editingDescriptionValue={editingDescriptionValue}
        comments={comments}
        loadingComments={loadingComments}
        newComment={newComment}
        submittingComment={submittingComment}
        childTasks={childTasks}
        loadingChildTasks={loadingChildTasks}
        handleCloseTaskDetail={handleCloseTaskDetail}
        setIsEditingDetail={setIsEditingDetail}
        handleUpdateTaskFromDetail={handleUpdateTaskFromDetail}
        handleStartEditDescription={handleStartEditDescription}
        setEditingDescriptionMode={setEditingDescriptionMode}
        setEditingDescriptionValue={setEditingDescriptionValue}
        handleSaveDescription={handleSaveDescription}
        setNewComment={setNewComment}
        handleSubmitComment={handleSubmitComment}
        handleOpenChildTaskModal={handleOpenChildTaskModal}
        setSelectedTaskForDetail={setSelectedTaskForDetail}
      />

      <ChildTaskModal
        showChildTaskModal={showChildTaskModal}
        childTaskFormData={childTaskFormData}
        usersList={usersList}
        updatingTask={updatingTask}
        selectedTaskForDetail={selectedTaskForDetail}
        handleCloseChildTaskModal={handleCloseChildTaskModal}
        handleChildTaskFormChange={handleChildTaskFormChange}
        handleCreateChildTask={handleCreateChildTask}
      />
    </>
  );
};

export default TaskManagement;
